import React, { useCallback, useReducer } from 'react';
import { Button, Stack, Overlay, Text } from '@moda/om';
import { reportError } from '../../../lib/reportError';
import { createUserApiError } from '../../../lib/createUserApiError';
import { useErrorModal } from '../../../hooks/useErrorModal';
import { useForm } from '../../../hooks/useForm';
import {
  useCreateAddressMutation,
  useEditAddressMutation,
  RestAddressAttributes,
  RestAddress
} from '../../../hooks/useAddress';
import { LoadingBalls } from '../../LoadingBalls';
import { FormButtonsLayout } from '../../FormButtonsLayout';
import { ShippingAddressFormFields } from './ShippingAddressFormFields';
import { ShippingAddressValidationPopup } from './ShippingAddressValidationPopup';
import { validations } from './validations';

interface Props {
  address?: RestAddress;
  submitButtonLabel?: string;
  onCancel?: () => void;
  onSave: (address?: RestAddress) => void | Promise<void>;
}

enum Mode {
  Resting,
  Validating,
  Saving
}

type State = {
  mode: Mode;
  attributesToValidate?: RestAddressAttributes;
};

type Action =
  | { type: 'START_VALIDATING'; payload: RestAddressAttributes }
  | { type: 'STOP_VALIDATING' }
  | { type: 'START_SAVING' }
  | { type: 'STOP_SAVING' };

const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'START_VALIDATING':
      return {
        mode: Mode.Validating,
        attributesToValidate: action.payload
      };
    case 'STOP_VALIDATING':
      return { mode: Mode.Resting };
    case 'START_SAVING':
      return { mode: Mode.Saving };
    case 'STOP_SAVING':
      return { mode: Mode.Resting };
    default:
      return state;
  }
};

export const ShippingAddressForm: React.FC<Props> = ({
  address,
  submitButtonLabel = 'Save',
  onSave,
  onCancel
}) => {
  const { showErrorModal } = useErrorModal();

  const [createAddress, { error: createError }] = useCreateAddressMutation();
  const [editAddress, { error: editError }] = useEditAddressMutation();

  const [state, dispatch] = useReducer(reducer, { mode: Mode.Resting });
  const { mode, attributesToValidate } = state;

  const startValidation = useCallback((attributes: RestAddressAttributes) => {
    dispatch({ type: 'START_VALIDATING', payload: attributes });
  }, []);

  const stopValidation = useCallback(() => {
    dispatch({ type: 'STOP_VALIDATING' });
  }, []);

  const { handleSubmit: handleSubmitForValidation, errors: validationErrors } = useForm({
    onSubmit: startValidation,
    validations
  });

  const handleSubmit = useCallback(
    async (attributes: RestAddressAttributes) => {
      dispatch({ type: 'START_SAVING' });

      try {
        const result = address
          ? await editAddress({ ...address, attributes })
          : await createAddress(attributes);
        await onSave(result);
      } catch (error) {
        const userApiError = createUserApiError(error);
        if (userApiError && (Object.keys(userApiError).length === 0 || userApiError.network)) {
          showErrorModal(error);
          reportError(error);
        }
      }

      dispatch({ type: 'STOP_SAVING' });
    },
    [address, createAddress, editAddress, showErrorModal, onSave]
  );

  return (
    <>
      <form className="ShippingAddressForm" onSubmit={handleSubmitForValidation}>
        <Stack space={4}>
          <ShippingAddressFormFields
            address={address}
            mutationFieldErrors={{ ...createError, ...editError }}
            validationErrors={validationErrors}
          />
          <FormButtonsLayout>
            {onCancel && (
              <Button onClick={onCancel} secondary type="button">
                Cancel
              </Button>
            )}
            <Button type="submit">{submitButtonLabel}</Button>
          </FormButtonsLayout>
        </Stack>
      </form>
      {mode === Mode.Validating && attributesToValidate && (
        <ShippingAddressValidationPopup
          attributes={attributesToValidate}
          onSubmit={handleSubmit}
          onClose={stopValidation}
        />
      )}
      <Overlay show={mode === Mode.Saving || mode === Mode.Validating}>
        <LoadingBalls />
        <Text treatment="eyebrow">Saving</Text>
      </Overlay>
    </>
  );
};
